import * as React from "react";
import { useState, useEffect } from "react";
import { useStyles } from "../../assets/styles/Theme";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  Autocomplete,

} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { AnyForNow } from "../../features/grn/types";

interface GrnSupplierSearchProps {
  supplierOptions: AnyForNow[];
  selectedSupplier: AnyForNow;
  setSelectedSupplier: (supplier: AnyForNow) => void;
  searchBy: string;
  setSearchBy: (searchBy: string) => void;
  onAddNewSupplier?: () => void;
  rightSidebarOpen: boolean;
  setRightSidebarOpen: (open: boolean) => void;
  toggleFilter: () => void;
  toggleRightSidebar: () => void;
}

const GrnSupplierSearchComponent: React.FC<GrnSupplierSearchProps> = (props) => {
  const {
    supplierOptions,
    selectedSupplier,
    setSelectedSupplier,
    searchBy,
    setSearchBy,
    onAddNewSupplier,
    rightSidebarOpen,
    setRightSidebarOpen,
    toggleFilter,
    toggleRightSidebar,
  } = props;
  const theme = useTheme();
  const styles = useStyles(theme);

  const handleSearchByChange = React.useCallback((event) => {
    setSearchBy(event.target.value);
  }, [setSearchBy]);

  // const matchedSupplierOption = React.useMemo(() => {
  //   if (!selectedSupplier || !selectedSupplier.suprefcode) return null;
  //   return (
  //     supplierOptions.find(
  //       (opt) =>
  //         opt.optSupplierRefCode === selectedSupplier.suprefcode.toString()
  //     ) || null
  //   );
  // }, [selectedSupplier, supplierOptions]);

  // Track last matched option to prevent finding new object references
  const lastMatchedOptionRef = React.useRef<AnyForNow | null>(null);

  const matchedSupplierOption = React.useMemo(() => {
    if (!selectedSupplier) {
      lastMatchedOptionRef.current = null;
      return null;
    }

    const supplierRefCode =
      selectedSupplier.suprefcode?.toString() || selectedSupplier.optSupplierRefCode;

    if (!supplierRefCode) {
      lastMatchedOptionRef.current = null;
      return null;
    }

    // Check if last matched option still matches
    if (
      lastMatchedOptionRef.current &&
      lastMatchedOptionRef.current.optSupplierRefCode === supplierRefCode
    ) {
      return lastMatchedOptionRef.current;
    }

    // Find new match
    const matched = supplierOptions.find(
      (opt) => opt.optSupplierRefCode === supplierRefCode
    ) || null;

    lastMatchedOptionRef.current = matched;
    return matched;
  }, [selectedSupplier, supplierOptions]);

  const handleAutocompleteChange = React.useCallback((event, value) => {
    if (value && typeof value === "object") {
      setSelectedSupplier(value);
      setRightSidebarOpen(true);
    }
  }, [setSelectedSupplier, setRightSidebarOpen]);

  const getOptionLabel = React.useCallback((option) => {
    if (searchBy === "PO") return option.poNumber;
    // Combine both name and ID for better matching
    return `${option.optSupplierName} (${option.optSupplierRefCode})`;
  }, [searchBy]);

  const goToAddNewSupplier = React.useCallback((props) => (
    <Paper {...props}>
      {props.children}
      {searchBy === "Supplier" && (
        <Box
          sx={{
            p: 1,
            borderTop: `1px solid ${(theme.palette as any).greyscale.stroke_1}`,
          }}
        >
          {/* <Button
            variant="text"
            size="small"
            startIcon={<AddIcon />}
            sx={{
              ...styles.btn_text,
              textAlign: "left",
              justifyContent: "left",
            }}
            onClick={onAddNewSupplier}
            fullWidth
          >
            New Supplier
          </Button> */}
        </Box>
      )}
    </Paper>
  ), [searchBy, theme.palette]);

  return (
    <>
      <Grid container spacing={1} alignItems="center">
        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
          <Autocomplete
            id="sample-search"
            fullWidth
            disableClearable
            options={supplierOptions}
            value={matchedSupplierOption || null}
            slotProps={{
              paper: {
                sx: {
                  boxShadow: 2,
                },
              },
            }}
            // getOptionLabel={(option) =>
            //   searchBy === "PO" ? option.poNumber : option.optSupplierName
            // }
            getOptionLabel={getOptionLabel}
            onChange={handleAutocompleteChange}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  ...styles.box_space_between,
                  width: "100%",
                  borderBottom: 1,
                  borderColor: (theme.palette as any).greyscale.stroke_1,
                }}
              >
                <Box sx={{ width: "100%" }}>
                  <Typography variant="subtitle_s">
                    {searchBy === "PO"
                      ? option.poNumber
                      : option.optSupplierName}

                    {/* {searchBy === "PO" ? option.poDate : ""} */}
                  </Typography>
                  <Box display={"flex"} gap={4}>
                    <Box display={"flex"} gap={1}>
                      <Typography variant="body_xs" color="text.secondary">
                        Supplier Code :{" "}
                      </Typography>
                      <Typography variant="subtitle_xs" color="text.secondary">
                        {searchBy === "PO"
                          ? option.poSupplierCode
                          : option.optSupplierRefCode}
                      </Typography>
                    </Box>
                    <Box display={"flex"} gap={1}>
                      {searchBy === "PO" && (
                        <Typography variant="body_xs" color="text.secondary">
                          Supplier Name :
                        </Typography>
                      )}
                      {searchBy === "PO" && (
                        <Typography
                          variant="subtitle_xs"
                          color="text.secondary"
                        >
                          {option.poSupplierName}
                        </Typography>
                      )}
                    </Box>
                    <Box display={"flex"} gap={1}>
                      {searchBy === "PO" && (
                        <Typography
                          variant="subtitle_xs"
                          color="text.secondary"
                        >
                          {option.poDate}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={{ ...styles.btn_primary }}
                  onClick={(event) => {
                    // event.stopPropagation();

                    // Manually set the selected supplier on button click:
                    setSelectedSupplier(option);

                    // Open sidebar or any other logic:
                    setRightSidebarOpen(true);
                  }}
                >
                  Select
                </Button>
              </Box>
            )}
            PaperComponent={goToAddNewSupplier}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={`Search by ${
                  searchBy === "PO" ? "PO Number/STO" : "Supplier Name/Code"
                }...`}
                size="small"
              />
            )}
          />
        </Grid>
        <Grid
          size={{ xs: 12, sm: 4, md: 4, lg: 2, xl: 2 }}
          sx={{
            display: "flex",
            alignContent: "flex-end",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <FormControl variant="outlined" fullWidth size="small">
            <InputLabel>Search by</InputLabel>
            <Select
              value={searchBy}
              onChange={handleSearchByChange}
              label="Search by"
              MenuProps={{
                PaperProps: {
                  sx: {
                    boxShadow: 2, // shadow
                    // borderRadius: 2,
                  },
                },
              }}
            >
              <MenuItem value="PO">PO</MenuItem>
              <MenuItem value="Supplier">Supplier</MenuItem>
            </Select>
          </FormControl>

          <IconButton aria-label="search" sx={{ ...styles.btn_grn_search }}>
            <SearchIcon />
          </IconButton>
        </Grid>{" "}
        <Grid size={{ xs: 12, sm: 1, md: 1, lg: 2, xl: 2 }}>
          <IconButton
            aria-label="search"
            // disabled={searchBy !== "Supplier"}
            disabled
            onClick={toggleFilter}
          >
            <FilterAltOutlinedIcon />
          </IconButton>
        </Grid>
        <Grid size={{ xs: 12, sm: 1, md: 1, lg: 2, xl: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <IconButton onClick={toggleRightSidebar}>
              <ViewSidebarOutlinedIcon />
            </IconButton>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

// Memoize component with custom comparison to prevent unnecessary re-renders
const GrnSupplierSearch = React.memo(GrnSupplierSearchComponent, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render), false if different (re-render)
  return (
    prevProps.searchBy === nextProps.searchBy &&
    prevProps.rightSidebarOpen === nextProps.rightSidebarOpen &&
    prevProps.selectedSupplier === nextProps.selectedSupplier &&
    prevProps.supplierOptions === nextProps.supplierOptions &&
    prevProps.setSelectedSupplier === nextProps.setSelectedSupplier &&
    prevProps.setSearchBy === nextProps.setSearchBy &&
    prevProps.setRightSidebarOpen === nextProps.setRightSidebarOpen &&
    prevProps.toggleFilter === nextProps.toggleFilter &&
    prevProps.toggleRightSidebar === nextProps.toggleRightSidebar &&
    prevProps.onAddNewSupplier === nextProps.onAddNewSupplier
  );
});

export default GrnSupplierSearch;
